'use client';

import { useMemo, useState } from 'react';
import { diffWords, hasVisibleChange, type DiffToken } from '@/module-public/utils/textDiff';
import { formatArticleContent, renderSupMarkup } from '../../../utils/formatActContent';
import type { ActChangeEvent } from '@/api/services/actChanges.service';
import type { ChangesPageCopy } from '../../../utils/actsTranslations';

/**
 * One article's change, rendered as a unified inline word-diff: removed wording
 * struck-through (red), added wording highlighted (green), unchanged wording
 * plain. Both the old and the new text therefore appear in the rendered HTML
 * (server-rendered - this is a client component only for the expander toggle,
 * and Next renders it to HTML on the server).
 *
 * Long diffs are CSS-clamped with a "Pokaż całość" expander; the full text is
 * always in the DOM (only visually clamped), so crawlers and no-JS users get
 * everything.
 */

// Rendered text over this length starts collapsed.
const COLLAPSE_CHARS = 700;

function tokenStyle(type: DiffToken['type']): React.CSSProperties {
  if (type === 'added') {
    return {
      backgroundColor: 'color-mix(in srgb, var(--state-success) 22%, transparent)',
      color: 'var(--text-primary)',
      borderRadius: '2px',
      padding: '0 1px',
    };
  }
  if (type === 'removed') {
    return {
      textDecoration: 'line-through',
      color: 'var(--state-error)',
      opacity: 0.85,
    };
  }
  return {};
}

export default function ChangeDiffView({
  event,
  copy,
}: {
  event: ActChangeEvent;
  copy: ChangesPageCopy;
}) {
  const tokens = useMemo(
    () => diffWords(event.old_text, event.new_text),
    [event.old_text, event.new_text],
  );

  const totalLen = (event.old_text?.length ?? 0) + (event.new_text?.length ?? 0);
  const collapsible = totalLen > COLLAPSE_CHARS;
  const [expanded, setExpanded] = useState(false);

  // A 'modified' event whose diff carries no word-level change is parser
  // formatting drift, not an amendment - showing an all-kept "diff" would only
  // confuse. Added/removed events always render (their whole text is the diff).
  if (event.change_type === 'modified' && !hasVisibleChange(tokens)) {
    return null;
  }

  const badge =
    event.change_type === 'added'
      ? copy.typeAdded
      : event.change_type === 'removed'
        ? copy.typeRemoved
        : copy.typeModified;
  const badgeColor =
    event.change_type === 'added'
      ? 'var(--state-success)'
      : event.change_type === 'removed'
        ? 'var(--state-error)'
        : 'var(--interactive-primary)';

  const note =
    event.change_type === 'added'
      ? copy.addedArticleNote
      : event.change_type === 'removed'
        ? copy.removedArticleNote
        : null;

  return (
    <section
      className="rounded-lg p-4 sm:p-5 mb-4"
      style={{ backgroundColor: 'var(--surface-elevated)' }}
    >
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <h3 className="text-lg font-semibold text-[var(--text-primary)] m-0">
          {formatArticleContent(event.article_label)}
        </h3>
        <span
          className="text-xs font-semibold uppercase tracking-wide px-2 py-0.5 rounded"
          style={{ color: badgeColor, backgroundColor: 'var(--surface-secondary)' }}
        >
          {badge}
        </span>
      </div>

      {note && (
        <p className="text-sm text-[var(--text-secondary)] mb-2">{note}</p>
      )}

      <div
        className="text-[var(--text-primary)] leading-relaxed whitespace-pre-wrap break-words"
        style={
          collapsible && !expanded
            ? { maxHeight: '16rem', overflow: 'hidden', position: 'relative' }
            : undefined
        }
      >
        {/* Snapshots carry article indices as markup ("art. 479<sup>33</sup>").
            Rendered per token, so the diff itself is untouched. */}
        {tokens.map((tok, i) => (
          <span key={i} data-diff={tok.type} style={tokenStyle(tok.type)}>
            {renderSupMarkup(tok.value)}
          </span>
        ))}
      </div>

      {collapsible && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-2 text-sm font-medium hover:underline"
          style={{ color: 'var(--interactive-primary)' }}
        >
          {expanded ? copy.showLess : copy.showFull}
        </button>
      )}
    </section>
  );
}
